import {useMutation} from "@tanstack/react-query";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {
    FuelPurchase,
    FuelPurchaseRequestBody,
    SubmitFuelPurchaseResponse,
    TransactionsApi,
    VerifyFuelPurchaseResponse,
} from "@devour/client";
import getConfig from "@/utils/getConfig";

/**
 * Upserts a FUEL purchase quote. Call whenever the user adjusts amount or payment method.
 *
 * @param fuelPurchaseRequestBody - Purchase details (amount, paymentMethodId, etc.)
 * @returns The updated FuelPurchase with locked price/fiat/fuel values
 */
export function useUpsertFuelPurchase() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useMutation({
        mutationFn: async (fuelPurchaseRequestBody: FuelPurchaseRequestBody): Promise<FuelPurchase> => {
            return new TransactionsApi(getConfig(fullToken)).updateTransactionsFuelPurchases({fuelPurchaseRequestBody});
        },
        /*
         * No query invalidation — result is consumed directly via mutation data
         * meta.fullToken is read by LoadFuelPanel to pass to downstream mutations
         */
        meta: {fullToken},
    });
}

/**
 * Submits a finalized FUEL purchase for payment processing.
 *
 * @param id - The FuelPurchase ID returned from useUpsertFuelPurchase
 * @returns SubmitFuelPurchaseResponse — includes `nextAction` if 3DS authentication is required
 */
export function useSubmitFuelPurchase() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useMutation({
        mutationFn: async (id: string): Promise<SubmitFuelPurchaseResponse> => {
            return new TransactionsApi(getConfig(fullToken)).createTransactionsFuelPurchaseId({id});
        },
    });
}

/**
 * Verifies a FUEL purchase after 3DS authentication completes.
 *
 * @param id - The FuelPurchase ID returned from useUpsertFuelPurchase
 */
export function useVerifyFuelPurchase() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useMutation({
        mutationFn: async (id: string): Promise<VerifyFuelPurchaseResponse> => {
            return new TransactionsApi(getConfig(fullToken)).getTransactionsFuelPurchaseIdVerify({id});
        },
    });
}
